import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import css from './SingularLinksDomains.css';
import { Label, Button } from '../../../components/widgets';
import { GeneralPopup, PopupTypes } from '../../../components/partials';
import SubDomainLinkTable from './subdomains/SubDomainLinkTable';
import Snackbar from '../../../components/widgets/Snackbar/Snackbar';
import { DNS_ZONE } from './subdomains/consts';

const SingularLinksDomains = ({
    subDomains = [],
    onCreateClicked,
    subDomainsPage = { working: false, error: '', message: '' },
    onFavouriteClicked,
    onArchiveClicked,
    domainsLimit = 50,
    brandedDomainsLimit = 0,
    isAgency = false,
}) => {
    const [archiveCandidate, setArchiveCandidate] = useState(null);
    const [isCreatingDomain, setIsCreatingDomain] = useState(false);
    const { error: submitError, message: submitMessage, working } = subDomainsPage;
    const [lastShownMessage, setLastShownMessage] = useState('');
    const [isFirstRender, setIsFirstRender] = useState(true);

    useEffect(() => {
        if (isFirstRender) {
            setLastShownMessage(submitMessage || '');
            setIsFirstRender(false);
        }
    }, [isFirstRender, submitMessage]);

    useEffect(() => {
        if (working) {
            setLastShownMessage('');
        }
    }, [working]);

    const handleSnackbarClose = useCallback(() => {
        setLastShownMessage(submitMessage);
    }, [submitMessage]);

    const handleFavouriteToggle = useCallback(
        ({ subdomain, zone, isFavourite }) => {
            onFavouriteClicked(subdomain, zone, isFavourite);
        },
        [onFavouriteClicked]
    );

    const handleCreate = useCallback(
        (subDomainValue, zone, targetCNAME) => {
            if (subDomainValue && zone) {
                onCreateClicked(subDomainValue, zone, targetCNAME);
            }
        },
        [onCreateClicked]
    );

    const handleCancelCreate = useCallback(() => setIsCreatingDomain(false), []);

    const handleArchiveRequest = useCallback(({ subdomain, zone }) => setArchiveCandidate({ subdomain, zone }), []);

    const handleArchiveConfirm = useCallback(() => {
        if (archiveCandidate) {
            onArchiveClicked(archiveCandidate.subdomain, archiveCandidate.zone);
            setArchiveCandidate(null);
        }
    }, [archiveCandidate, onArchiveClicked]);

    const handleArchiveCancel = useCallback(() => setArchiveCandidate(null), []);

    const startCreateFlow = useCallback(() => setIsCreatingDomain(true), []);

    return (
        <>
            <div className={css.content}>
                <div className={css.subHeader}>
                    <Label className={css.label} text="STATIC.PAGES.MANAGE_LINKS.SUBDOMAIN_SHELF_TITLE" />
                    {!isAgency && (
                        <Button
                            disabled={isCreatingDomain}
                            type="primary"
                            onClick={startCreateFlow}
                            className={css.addButton}
                        >
                            <Translate id="STATIC.PAGES.MANAGE_LINKS.SUBDOMAIN_SHELF_CREATE" />
                        </Button>
                    )}
                </div>

                <Label
                    text="STATIC.PAGES.MANAGE_LINKS.SUBDOMAIN_SHELF_ADD"
                    textReplace={{
                        subDomainsCount: subDomains.filter(e => e.zone === DNS_ZONE).length,
                        limit: domainsLimit,
                        brandedDomainsLimit,
                        brandedDomainsCount: subDomains.filter(e => e.zone !== DNS_ZONE).length,
                    }}
                />

                <SubDomainLinkTable
                    onCreateLinksDomainClick={startCreateFlow}
                    onCreateClicked={handleCreate}
                    isCreatingDomain={isCreatingDomain}
                    onCancelCreateDomain={handleCancelCreate}
                    onFavouriteClicked={handleFavouriteToggle}
                    onArchiveClicked={handleArchiveRequest}
                    subDomains={subDomains}
                    domainsLimit={domainsLimit}
                    brandedDomainsLimit={brandedDomainsLimit}
                    isAgency={isAgency}
                    subDomainsPage={subDomainsPage}
                />
            </div>

            <GeneralPopup
                open={!!archiveCandidate}
                type={PopupTypes.WARNING_WITH_CANCEL}
                title="STATIC.PAGES.VIEW_DOMAINS.DELETE_WARNING"
                acceptText="STATIC.PAGES.VIEW_DOMAINS.ARCHIVE_YES"
                onAccept={handleArchiveConfirm}
                onReject={handleArchiveCancel}
            />

            <Snackbar
                title={submitMessage}
                type={submitError ? 'error' : 'success'}
                open={!!submitMessage && !working && submitMessage !== lastShownMessage && !isFirstRender}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
            />
        </>
    );
};

SingularLinksDomains.propTypes = {
    subDomains: PropTypes.arrayOf(
        PropTypes.shape({
            favourite: PropTypes.bool,
            zone: PropTypes.string.isRequired,
            fullDomain: PropTypes.string,
            subdomain: PropTypes.string.isRequired,
            status: PropTypes.string.isRequired,
        })
    ),
    onCreateClicked: PropTypes.func,
    subDomainsPage: PropTypes.shape({
        working: PropTypes.bool,
        error: PropTypes.string,
        message: PropTypes.string,
    }),
    onFavouriteClicked: PropTypes.func,
    onArchiveClicked: PropTypes.func,
    domainsLimit: PropTypes.number,
    brandedDomainsLimit: PropTypes.number,
    isAgency: PropTypes.bool,
};

export default SingularLinksDomains;
